import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaAward, 
  FaUsers, 
  FaHeart, 
  FaShieldAlt,
  FaLeaf,
  FaHandshake
} from 'react-icons/fa';

const AboutContainer = styled.div`
  padding-top: 120px;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-color) 0%, #e67e00 100%);
  color: var(--white-color);
  padding: 100px 0;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: var(--secondary-color);
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const AboutImage = styled.div`
  position: relative;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, var(--primary-color), transparent);
    opacity: 0.3;
  }
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AboutText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: var(--secondary-color);
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 60px;
`;

const ValueCard = styled(motion.div)`
  background: var(--white-color);
  padding: 40px 30px;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  text-align: center;
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: var(--white-color);
`;

const ValueTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--secondary-color);
`;

const ValueDescription = styled.p`
  color: var(--secondary-color);
  line-height: 1.6;
`;

const StatsSection = styled(Section)`
  background: var(--light-gray);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  text-align: center;
`;

const StatItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--secondary-color);
`;

const AboutPage = () => {
  const values = [
    {
      icon: <FaAward />,
      title: "Kalite",
      description: "En yüksek kalite standartlarında hizmet sunuyoruz."
    },
    {
      icon: <FaUsers />,
      title: "Güven",
      description: "Müşterilerimizin güvenini kazanmak önceliğimizdir."
    },
    {
      icon: <FaHeart />,
      title: "Sevgi",
      description: "Hayvanlara karşı sevgi ve saygı ile yaklaşıyoruz."
    },
    {
      icon: <FaShieldAlt />,
      title: "Güvenlik",
      description: "Tüm işlemlerimizde güvenlik ön plandadır."
    },
    {
      icon: <FaLeaf />,
      title: "Doğallık",
      description: "Doğal ve sağlıklı beslenme yöntemleri kullanıyoruz."
    },
    {
      icon: <FaHandshake />,
      title: "Dürüstlük",
      description: "Dürüst ve şeffaf iş ilişkileri kuruyoruz."
    }
  ];

  const stats = [
    { number: 25, label: "Yıllık Tecrübe" },
    { number: 120, label: "Profesyonel Ekip" },
    { number: 15000, label: "Başarılı İşlem" },
    { number: 100, label: "Müşteri Memnuniyeti %" }
  ];

  return (
    <AboutContainer>
      <HeroSection>
        <Container>
          <HeroTitle>Hakkımızda</HeroTitle>
          <HeroSubtitle>
            25 yılı aşkın tecrübemizle besi sektöründe güvenilir çözümler sunuyoruz.
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <AboutGrid>
            <AboutImage>
              <img 
                src="/images/about/hakkimizda.jpg" 
                alt="SULTANLAR BESİCİLİK Çiftliği"
              />
            </AboutImage>
            <AboutContent>
              <SectionTitle>Bizim Hikayemiz</SectionTitle>
              <AboutText>
                1999 yılında Burdur'da kurulan SULTANLAR BESİCİLİK, besi sektöründe 
                kaliteli hizmet anlayışıyla yola çıktı. Çeyrek asırlık deneyimimizle, 
                müşterilerimize en iyi hizmeti sunmak için sürekli kendimizi geliştiriyoruz.
              </AboutText>
              <AboutText>
                Uzman ekibimiz ve modern tesislerimizle, hayvan yetiştirme ve satışında 
                sektörün öncü firmalarından biri haline geldik. Müşteri memnuniyeti 
                odaklı yaklaşımımızla, her işlemimizde kaliteyi ön planda tutuyoruz.
              </AboutText>
              <AboutText>
                Bugün 120 kişilik profesyonel ekibimiz ve 15.000'den fazla başarılı 
                işlemimizle, besi sektöründe güvenilir bir marka olarak hizmet vermeye 
                devam ediyoruz.
              </AboutText>
            </AboutContent>
          </AboutGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle>Değerlerimiz</SectionTitle>
          <SectionSubtitle>
            İşimizi yaparken öncelik verdiğimiz temel değerlerimiz
          </SectionSubtitle>
          
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </Container>
      </Section>

      <StatsSection>
        <Container>
          <SectionTitle>Rakamlarla Biz</SectionTitle>
          <SectionSubtitle>
            Başarılarımızı rakamlarla gösteriyoruz
          </SectionSubtitle>
          
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatItem
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <StatNumber>{stat.number}+</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatItem>
            ))}
          </StatsGrid>
        </Container>
      </StatsSection>
    </AboutContainer>
  );
};

export default AboutPage; 